import { CaretDownOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useModel } from '@umijs/max';
import { Select } from 'antd';
import { createStyles } from 'antd-style';
import React, { useMemo } from 'react';
import { getStoredDistrictCode, setSelectedDistrictCode } from '@/utils/tools';

import {
  buildDistrictSelectOptions,
  type DistrictEnumProvinceItem,
  type DistrictSelectOption,
} from '@/utils/emnu';

const getShortDistrictName = (label?: string) => {
  if (!label) return '';
  const parts = label.split('-');
  return parts[parts.length - 1] || label;
};

const useStyles = createStyles(({ token }) => ({
  wrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    height: 36,
    padding: '0 14px',
    marginRight: 12,
    borderRadius: 18,
    border: `1px solid ${token.colorPrimaryBorder}`,
    boxShadow: `0 1px 2px ${token.colorPrimaryBgHover}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: token.colorPrimary,
      boxShadow: `0 2px 8px ${token.colorPrimaryBgHover}`,
    },
  },
  icon: {
    color: token.colorPrimary,
    fontSize: 15,
    flexShrink: 0,
  },
  tag: {
    fontSize: 12,
    color: token.colorTextSecondary,
    lineHeight: 1,
    flexShrink: 0,
    userSelect: 'none',
  },
  select: {
    minWidth: 88,
    maxWidth: 132,
    flex: '0 1 auto',
    '& .ant-select-selector': {
      padding: '0 18px 0 0 !important',
      background: 'transparent !important',
      border: 'none !important',
      boxShadow: 'none !important',
      height: 'auto !important',
    },
    '& .ant-select-selection-item': {
      fontWeight: 600,
      fontSize: 14,
      color: token.colorPrimary,
      paddingInlineEnd: '0 !important',
    },
    '& .ant-select-arrow': {
      color: token.colorPrimary,
      right: 0,
      fontSize: 10,
    },
  },
  popupOption: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    lineHeight: 1.4,
    paddingBlock: 2,
  },
  popupDistrict: {
    fontWeight: 500,
    color: token.colorText,
  },
  popupPath: {
    fontSize: 12,
    color: token.colorTextTertiary,
  },
}));

const HeaderDistrictSelect: React.FC = () => {
  const { styles } = useStyles();
  const { initialState } = useModel('@@initialState');
  const districtEnum = useMemo<DistrictEnumProvinceItem[]>(
    () =>
      Array.isArray(initialState?.currentUser?.districtEnum)
        ? (initialState.currentUser.districtEnum as DistrictEnumProvinceItem[])
        : [],
    [initialState?.currentUser?.districtEnum],
  );
  const options = useMemo(
    () => buildDistrictSelectOptions(districtEnum),
    [districtEnum],
  );
  const selectedDistrictCode =
    initialState?.selectedDistrictCode ?? getStoredDistrictCode();

  const displayOptions = useMemo(
    () =>
      options.map((option) => {
        const parts = option.label.split('-');
        const districtName = parts[parts.length - 1] || option.label;
        const path = parts.slice(0, -1).join(' / ');
        return {
          ...option,
          districtName,
          path,
        };
      }),
    [options],
  );

  if (!options.length || selectedDistrictCode == null) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <EnvironmentOutlined className={styles.icon} />
      <span className={styles.tag}>区县</span>
      <Select
        className={styles.select}
        value={selectedDistrictCode}
        options={displayOptions}
        variant="borderless"
        showSearch
        popupMatchSelectWidth={260}
        suffixIcon={<CaretDownOutlined />}
        optionFilterProp="label"
        labelRender={(option) => getShortDistrictName(option.label as string)}
        optionRender={(option) => {
          const item = option.data as DistrictSelectOption & {
            districtName?: string;
            path?: string;
          };
          return (
            <div className={styles.popupOption}>
              <span className={styles.popupDistrict}>
                {item.districtName || option.label}
              </span>
              {item.path ? (
                <span className={styles.popupPath}>{item.path}</span>
              ) : null}
            </div>
          );
        }}
        onChange={(value: number) => {
          setSelectedDistrictCode(value);
          window.location.reload();
        }}
      />
    </div>
  );
};

export default HeaderDistrictSelect;
